import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useToken } from "@/lib/session";
import BrandLogo from "@/components/BrandLogo";
import { ArrowLeft, Trophy, Sparkles, Brain, Grid3x3, Type, HelpCircle } from "lucide-react";

const GAME_STYLE = {
  Rebus: { icon: Brain, color: "#39FF14" },
  Sudoku: { icon: Grid3x3, color: "#1E88E5" },
  "Word Building": { icon: Type, color: "#FFD600" },
  Trivia: { icon: HelpCircle, color: "#FF3B30" },
  Bonus: { icon: Sparkles, color: "#FF9800" },
};

export default function PointsLogPage() {
  const token = useToken();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/employee/points/log?token=${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [token]);

  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b-4 border-black px-6 py-4 flex items-center justify-between sticky top-0 bg-[#FDFBF7] z-10">
        <BrandLogo size="md" title="TekWissen Fun Zone" subtitle="My Points Log" />
        <button data-testid="points-back-btn" onClick={() => navigate("/play")} className="brutal-btn-secondary text-sm flex items-center gap-2">
          <ArrowLeft size={16} /> Back
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <div className="brutal-card p-6 flex items-center justify-between" data-testid="points-total-card">
          <div>
            <div className="text-xs font-black uppercase text-neutral-500">Your Total Points</div>
            <div className="text-5xl font-black tracking-tighter" data-testid="points-total">{data ? data.total : "—"}</div>
          </div>
          <div className="w-16 h-16 border-4 border-black flex items-center justify-center bg-[#FFD600]"><Trophy size={30} /></div>
        </div>

        <div className="brutal-card p-0 overflow-hidden">
          <div className="px-4 py-3 border-b-2 border-black font-black uppercase text-sm bg-black text-white">Points History</div>
          {err ? (
            <div className="p-6 text-center text-sm text-[#FF3B30]">Couldn't load your points log. Please try again.</div>
          ) : !data ? (
            <div className="p-6 text-center text-sm text-neutral-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500" data-testid="points-empty">No points yet — play today's games to start earning! Solve all three (Rebus, Sudoku, Word Building) for a <strong>+10 bonus</strong>.</div>
          ) : (
            <div className="divide-y-2 divide-neutral-200" data-testid="points-log-list">
              {items.map((it, i) => {
                const st = GAME_STYLE[it.game] || GAME_STYLE.Bonus;
                const Icon = st.icon;
                return (
                  <div key={i} className="flex items-center gap-3 px-4 py-3" data-testid="points-log-row">
                    <div className="w-9 h-9 border-2 border-black flex items-center justify-center flex-shrink-0" style={{ background: st.color }}><Icon size={16} className="text-black" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm">{it.game} <span className="font-normal text-neutral-500">— {it.reason}</span></div>
                      <div className="text-xs text-neutral-400 font-mono">{it.date}</div>
                    </div>
                    <div className={`font-black text-lg whitespace-nowrap ${it.points >= 0 ? "text-[#16a34a]" : "text-[#FF3B30]"}`}>+{it.points}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <p className="text-xs text-neutral-500 text-center">Correct answers = 30 pts · all 3 games in a day = +10 (100 total) · 5-day all-games streak = +50.</p>
      </main>
    </div>
  );
}
